'use client';

/**
 * TokenEditor — the §7 constructor field: a contentEditable input where
 * `{{variables}}` and `{{ai}}` blocks render as lego-style bricks recruiters
 * treat as single characters (backspace removes a whole brick), while the
 * value stays the raw token string the server resolves.
 *
 * The DOM is the source of truth while typing (no caret-jumping rebuilds): the
 * editor only re-renders from `value` when it differs from the last string it
 * emitted (node switch, external reset). AI blocks embed an editable prompt
 * island — the instruction is typed straight into the box and serialized as
 * `{{ai: prompt}}`.
 *
 * Bricks are inserted via the slash menu (type "/" for a Claude-style picker of
 * variables + the AI block — no always-visible palette) and are draggable:
 * within an editor (reorder) and between editors (subject ↔ body, module-level
 * drag state). Drops land at the caret position under the pointer; native
 * text-selection drags are left to the browser.
 */

import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { MESSAGE_VARIABLES, MESSAGE_VARIABLE_LABELS, type AiPartConfig } from '../types';
import {
    DRAG_MIME_AI,
    DRAG_MIME_AI2,
    DRAG_MIME_VARIABLE,
    aiPartLabel,
    buildAiAdvBlockEl,
    buildAiBlockEl,
    buildMessageFragment,
    buildVariableChipEl,
    newAiPartId,
    serializeAiBlockEl,
    serializeEditorDom,
    variableChipClass,
} from '../message-tokens';

/** The brick being dragged between/within TokenEditors (module-wide so a drop
 *  in one editor can re-serialize the source editor it left). */
let activeDrag: { el: HTMLElement; sourceRoot: HTMLElement; notify: () => void } | null = null;

/* ─── slash menu (type "/" to insert a brick, Claude-skills style) ─── */

// `unsubscribe.link` is system-injected into the footer — not offered for insert.
const SLASH_VARIABLES = MESSAGE_VARIABLES.filter((v) => v !== 'unsubscribe.link');

type SlashItem =
    | { kind: 'variable'; token: string; label: string }
    | { kind: 'ai'; label: string }
    | { kind: 'ai2'; label: string };

function slashItemsFor(query: string, withAdvanced: boolean): SlashItem[] {
    const q = query.trim().toLowerCase();
    const vars: SlashItem[] = SLASH_VARIABLES.filter(
        (v) => !q || MESSAGE_VARIABLE_LABELS[v].toLowerCase().includes(q) || v.toLowerCase().includes(q)
    ).map((v) => ({ kind: 'variable', token: v, label: MESSAGE_VARIABLE_LABELS[v] }));
    const ai: SlashItem[] = !q || 'ai writes'.includes(q) ? [{ kind: 'ai', label: 'AI writes…' }] : [];
    const ai2: SlashItem[] =
        withAdvanced && (!q || 'ai writes advanced'.includes(q))
            ? [{ kind: 'ai2', label: 'AI writes — advanced…' }]
            : [];
    return [...vars, ...ai, ...ai2];
}

/** Where the slash query sits: the text node holding "/query" before the caret. */
type SlashContext = { node: Text; start: number; end: number; query: string };

export type TokenEditorHandle = {
    /** Insert a `{{variable}}` brick at the caret (or at the end). */
    insertVariable: (token: string) => void;
    /** Insert an `{{ai}}` brick and focus its prompt input. */
    insertAiBlock: () => void;
    focus: () => void;
};

type TokenEditorProps = {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    /** Single-line fields (subject) refuse newlines. Defaults to multiline. */
    multiline?: boolean;
    minHeight?: number;
    /** Raw-string budget (bricks count at full `{{…}}` length), e.g. 300 for invite notes. */
    maxLength?: number;
    onFocus?: () => void;
    ariaLabel?: string;
    /** Whether AI blocks personalize per candidate. AI copy is static (one text
     *  for everyone) on every channel now, so this defaults to false. */
    aiPerRecipient?: boolean;
    /** Advanced `{{ai2:<id>}}` part configs (from node.message.aiParts) — labels
     *  the chips. Presence of `onAiAdvOpen` enables the advanced slash option. */
    aiParts?: Record<string, AiPartConfig>;
    /** Open the instructions/examples modal for an advanced part. Called on chip
     *  click and right after inserting a new advanced block. */
    onAiAdvOpen?: (id: string) => void;
};

export const TokenEditor = forwardRef<TokenEditorHandle, TokenEditorProps>(function TokenEditor(
    {
        value,
        onChange,
        placeholder,
        multiline = true,
        minHeight,
        maxLength,
        onFocus,
        ariaLabel,
        aiPerRecipient = false,
        aiParts,
        onAiAdvOpen,
    },
    ref
) {
    const rootRef = useRef<HTMLDivElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const lastEmitted = useRef<string | null>(null);
    // Latest emit, callable from module-level drag state without going stale.
    const emitRef = useRef<() => void>(() => {});

    // Slash menu: opens when the caret sits after "/query" (typed text only).
    // Anchored by its bottom edge so the list grows UPWARD from the caret line.
    const [menu, setMenu] = useState<{ query: string; x: number; bottom: number } | null>(null);
    const [menuIndex, setMenuIndex] = useState(0);
    const menuItems = menu ? slashItemsFor(menu.query, !!onAiAdvOpen) : [];

    /* ── DOM ← value, only when the change didn't originate here ── */
    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root || value === lastEmitted.current) return;
        root.replaceChildren(buildMessageFragment(document, value, aiPerRecipient, aiParts));
        ensureTrail(root);
        lastEmitted.current = value;
    }, [value]);

    /* ── Advanced chip labels track their config without a DOM rebuild (the
          body string doesn't change when the modal saves) ── */
    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        root.querySelectorAll<HTMLElement>('[data-ai-adv-block]').forEach((el) => {
            const label = el.querySelector('[data-ai-adv-label]');
            if (label) label.textContent = aiPartLabel(aiParts?.[el.dataset.aiAdvBlock ?? '']);
        });
    }, [aiParts]);

    function emit() {
        const root = rootRef.current;
        if (!root) return;
        let s = serializeEditorDom(root);
        if (!multiline) s = s.replace(/\n+/g, ' ');
        lastEmitted.current = s;
        onChange(s);
    }
    emitRef.current = emit;

    function currentLength(): number {
        const root = rootRef.current;
        return root ? serializeEditorDom(root).length : value.length;
    }

    /** Selection range if it lives inside this editor, else null. */
    function rawSelectionRange(): Range | null {
        const root = rootRef.current;
        const sel = window.getSelection();
        if (!root || !sel || sel.rangeCount === 0) return null;
        const r = sel.getRangeAt(0);
        return root.contains(r.commonAncestorContainer) ? r : null;
    }

    /** Where new bricks land: the caret — bumped out of AI prompt islands so a
     *  brick never nests inside a block — or the end of the content. */
    function insertionRange(): Range {
        const root = rootRef.current!;
        const raw = rawSelectionRange();
        if (raw) {
            const host = closestEl(raw.commonAncestorContainer, '[data-ai-block]');
            if (!host) return raw;
            const after = document.createRange();
            after.setStartAfter(host);
            after.collapse(true);
            return after;
        }
        const end = document.createRange();
        const trail = root.querySelector(':scope > [data-ote-trail]');
        if (trail) end.setStartBefore(trail);
        else {
            end.selectNodeContents(root);
            end.collapse(false);
        }
        end.collapse(true);
        return end;
    }

    function placeCaret(positioner: (r: Range) => void, focusEl?: HTMLElement) {
        (focusEl ?? rootRef.current)?.focus();
        const sel = window.getSelection();
        if (!sel) return;
        const r = document.createRange();
        positioner(r);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
    }

    function insertNodeAtCaret(node: Node, caretInside?: HTMLElement | null) {
        const root = rootRef.current;
        if (!root) return;
        const r = insertionRange();
        r.deleteContents();
        r.insertNode(node);
        ensureTrail(root);
        if (caretInside) placeCaret((nr) => nr.selectNodeContents(caretInside), caretInside);
        else placeCaret((nr) => nr.setStartAfter(node));
        emit();
    }

    function insertPlainText(text: string) {
        const tn = document.createTextNode(text);
        insertNodeAtCaret(tn);
    }

    /* ── slash menu ── */

    /** The "/query" run directly before a collapsed caret, if any. Plain text
     *  only — never inside an AI prompt island. */
    function slashContext(): SlashContext | null {
        const root = rootRef.current;
        const sel = window.getSelection();
        if (!root || !sel || !sel.isCollapsed || sel.rangeCount === 0) return null;
        const r = sel.getRangeAt(0);
        if (!root.contains(r.startContainer) || r.startContainer.nodeType !== Node.TEXT_NODE) return null;
        if (closestEl(r.startContainer, '[data-ai-prompt]')) return null;
        const node = r.startContainer as Text;
        const upto = (node.nodeValue ?? '').slice(0, r.startOffset);
        const m = /(^|[\s ])\/([\w .-]*)$/.exec(upto);
        if (!m) return null;
        return { node, start: upto.length - m[0].length + m[1].length, end: r.startOffset, query: m[2] };
    }

    /** Re-anchor (or close) the menu from the current caret. Runs after every
     *  input/click — the menu exists only while a live "/query" precedes it. */
    function updateSlashMenu() {
        const ctx = slashContext();
        const wrap = wrapRef.current;
        if (!ctx || !wrap) {
            setMenu(null);
            return;
        }
        const r = document.createRange();
        r.setStart(ctx.node, ctx.start);
        r.setEnd(ctx.node, ctx.end);
        const rect = r.getBoundingClientRect();
        const wrect = wrap.getBoundingClientRect();
        setMenu({
            query: ctx.query,
            x: Math.max(0, Math.min(rect.left - wrect.left, wrect.width - 230)),
            bottom: wrect.bottom - rect.top + 4,
        });
        setMenuIndex(0);
    }

    /** Swap the typed "/query" for the chosen brick. */
    function applySlashItem(item: SlashItem) {
        const ctx = slashContext();
        setMenu(null);
        if (!ctx) return;
        const range = document.createRange();
        range.setStart(ctx.node, ctx.start);
        range.setEnd(ctx.node, ctx.end);
        range.deleteContents();
        const sel = window.getSelection();
        if (sel) {
            sel.removeAllRanges();
            sel.addRange(range);
        }
        if (item.kind === 'variable') {
            if (maxLength && currentLength() + item.token.length + 4 > maxLength) return;
            insertNodeAtCaret(buildVariableChipEl(document, item.token));
        } else if (item.kind === 'ai2') {
            if (maxLength && currentLength() + 16 > maxLength) return;
            const id = newAiPartId();
            insertNodeAtCaret(buildAiAdvBlockEl(document, id, aiParts?.[id]));
            // Straight into configuration — an advanced block without
            // instructions is just a slower basic block.
            onAiAdvOpen?.(id);
        } else {
            if (maxLength && currentLength() + 7 > maxLength) return;
            const block = buildAiBlockEl(document, '', aiPerRecipient);
            const island = block.querySelector<HTMLElement>('[data-ai-prompt]');
            insertNodeAtCaret(block, island);
        }
    }

    useImperativeHandle(ref, () => ({
        insertVariable(token: string) {
            if (maxLength && currentLength() + token.length + 4 > maxLength) return;
            insertNodeAtCaret(buildVariableChipEl(document, token));
        },
        insertAiBlock() {
            if (maxLength && currentLength() + 7 > maxLength) return;
            const block = buildAiBlockEl(document, '', aiPerRecipient);
            const island = block.querySelector<HTMLElement>('[data-ai-prompt]');
            insertNodeAtCaret(block, island);
        },
        focus() {
            rootRef.current?.focus();
        },
    }));

    /* ── handlers ── */

    function handleInput() {
        const root = rootRef.current;
        if (!root) return;
        ensureTrail(root);
        // Browsers park a <br> in emptied prompt islands — that defeats the
        // :empty placeholder, so clear it.
        root.querySelectorAll('[data-ai-prompt]').forEach((p) => {
            if (!p.textContent) p.replaceChildren();
        });
        emit();
        updateSlashMenu();
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        const target = e.target as HTMLElement;
        const inPrompt = !!target.closest?.('[data-ai-prompt]');
        // Slash menu owns navigation keys while it's open.
        if (menu && menuItems.length > 0) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                const d = e.key === 'ArrowDown' ? 1 : -1;
                setMenuIndex((i) => (i + d + menuItems.length) % menuItems.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                applySlashItem(menuItems[menuIndex]);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setMenu(null);
                return;
            }
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (inPrompt) {
                // Commit the prompt: hop out, caret right after the brick.
                const block = target.closest('[data-ai-block]') as HTMLElement | null;
                if (block) placeCaret((r) => r.setStartAfter(block));
            } else if (multiline) {
                insertPlainText('\n');
            }
            return;
        }
        if (e.key === 'Escape' && inPrompt) {
            e.preventDefault();
            const block = target.closest('[data-ai-block]') as HTMLElement | null;
            if (block) placeCaret((r) => r.setStartAfter(block));
        }
    }

    function handleBeforeInput(e: React.FormEvent<HTMLDivElement>) {
        const ne = e.nativeEvent as InputEvent;
        const inputType = ne.inputType || '';
        if (!inputType.startsWith('insert')) return;
        if (!multiline && (inputType === 'insertParagraph' || inputType === 'insertLineBreak')) {
            e.preventDefault();
            return;
        }
        if (maxLength && currentLength() >= maxLength) e.preventDefault();
    }

    function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
        e.preventDefault();
        let text = e.clipboardData.getData('text/plain');
        if (!text) return;
        const inPrompt = !!(e.target as HTMLElement).closest?.('[data-ai-prompt]');
        if (!multiline || inPrompt) text = text.replace(/\s*\n+\s*/g, ' ');
        if (inPrompt) text = text.replace(/[{}]/g, ' ');
        if (maxLength) {
            text = text.slice(0, Math.max(0, maxLength - currentLength()));
            if (!text) return;
        }
        const root = rootRef.current;
        if (!root) return;
        if (inPrompt) {
            // Plain text only inside the prompt island, at its own caret.
            const r = rawSelectionRange();
            if (!r) return;
            r.deleteContents();
            const tn = document.createTextNode(text);
            r.insertNode(tn);
            placeCaret((nr) => nr.setStartAfter(tn), (e.target as HTMLElement).closest('[data-ai-prompt]') as HTMLElement);
            emit();
            return;
        }
        // Pasted copy may itself contain {{tokens}} — they become bricks.
        const frag = buildMessageFragment(document, text, aiPerRecipient, aiParts);
        const lastNode = frag.lastChild;
        const r = insertionRange();
        r.deleteContents();
        r.insertNode(frag);
        ensureTrail(root);
        if (lastNode) placeCaret((nr) => nr.setStartAfter(lastNode));
        emit();
    }

    function handleClick(e: React.MouseEvent<HTMLDivElement>) {
        const target = e.target as HTMLElement;
        const btn = target.closest?.('[data-ai-remove]');
        if (!btn) {
            // A click on an advanced chip (not its ×) opens its config modal.
            const adv = target.closest?.('[data-ai-adv-block]') as HTMLElement | null;
            if (adv && onAiAdvOpen) {
                e.preventDefault();
                onAiAdvOpen(adv.dataset.aiAdvBlock ?? '');
                return;
            }
            // A click moves the caret — re-anchor or close the slash menu.
            updateSlashMenu();
            return;
        }
        e.preventDefault();
        const root = rootRef.current;
        const block = btn.closest('[data-ai-block], [data-ai-adv-block]');
        block?.remove();
        if (root) {
            ensureTrail(root);
            root.focus();
        }
        emit();
    }

    /* ── drag & drop: bricks move within/between editors; palette bricks copy in ── */

    function handleDragStart(e: React.DragEvent<HTMLDivElement>) {
        const root = rootRef.current;
        const target = e.target as HTMLElement;
        // Drag handles: a variable chip, or the "AI writes" tag of an AI box.
        const handle = target.closest?.('[data-token], .ot-ai-tag');
        if (!handle || !root) return; // plain text-selection drags stay native
        const block = handle.closest('[data-token], [data-ai-block], [data-ai-adv-block]') as HTMLElement | null;
        if (!block || !root.contains(block)) return;
        const token = block.dataset.token;
        const advId = block.dataset.aiAdvBlock;
        const raw = token ? `{{${token}}}` : advId ? `{{ai2: ${advId}}}` : serializeAiBlockEl(block);
        e.dataTransfer.setData('text/plain', raw);
        if (token) e.dataTransfer.setData(DRAG_MIME_VARIABLE, token);
        else if (advId) e.dataTransfer.setData(DRAG_MIME_AI2, advId);
        else e.dataTransfer.setData(DRAG_MIME_AI, block.querySelector('[data-ai-prompt]')?.textContent ?? '');
        e.dataTransfer.effectAllowed = 'copyMove';
        activeDrag = { el: block, sourceRoot: root, notify: () => emitRef.current() };
    }

    function handleDragEnd() {
        // Fires on the source after the drop (or a cancelled drag) — a
        // successful drop already cleared it.
        activeDrag = null;
        rootRef.current?.classList.remove('ot-token-editor--dropping');
    }

    function isBrickDrag(dt: DataTransfer): boolean {
        const types = Array.from(dt.types || []);
        return (
            !!activeDrag ||
            types.includes(DRAG_MIME_VARIABLE) ||
            types.includes(DRAG_MIME_AI) ||
            types.includes(DRAG_MIME_AI2)
        );
    }

    function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
        if (!isBrickDrag(e.dataTransfer)) return; // native text drops handle themselves
        e.preventDefault();
        e.dataTransfer.dropEffect = activeDrag ? 'move' : 'copy';
        rootRef.current?.classList.add('ot-token-editor--dropping');
    }

    function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
        const root = rootRef.current;
        if (root && !root.contains(e.relatedTarget as Node)) root.classList.remove('ot-token-editor--dropping');
    }

    function handleDrop(e: React.DragEvent<HTMLDivElement>) {
        const root = rootRef.current;
        if (!root) return;
        root.classList.remove('ot-token-editor--dropping');
        const dt = e.dataTransfer;
        if (!isBrickDrag(dt)) return; // let the browser insert plain text; input event emits
        e.preventDefault();

        const drag = activeDrag;
        activeDrag = null;
        const range = rangeFromPoint(e.clientX, e.clientY, root);

        if (drag) {
            // Moving an existing brick. Dropping it onto itself is a no-op.
            if (drag.el.contains(range.startContainer) || drag.el === range.startContainer) return;
            const sameEditor = drag.sourceRoot === root;
            if (!sameEditor && maxLength) {
                const raw = drag.el.dataset.token ? `{{${drag.el.dataset.token}}}` : serializeAiBlockEl(drag.el);
                if (currentLength() + raw.length > maxLength) return;
            }
            range.insertNode(drag.el); // relocates the node
            ensureTrail(root);
            if (!sameEditor) {
                ensureTrail(drag.sourceRoot);
                drag.notify();
            }
            placeCaret((nr) => nr.setStartAfter(drag.el));
            emit();
            return;
        }

        // Copy in from the palette. An advanced brick can't copy across editors
        // (its config lives on the source node's message) — move-drags handled
        // above via activeDrag; a stray ai2 payload with no live drag is dropped.
        if (dt.getData(DRAG_MIME_AI2)) return;
        const token = dt.getData(DRAG_MIME_VARIABLE);
        const raw = token ? `{{${token}}}` : '{{ai}}';
        if (maxLength && currentLength() + raw.length > maxLength) return;
        if (token) {
            const chip = buildVariableChipEl(document, token);
            range.insertNode(chip);
            ensureTrail(root);
            placeCaret((nr) => nr.setStartAfter(chip));
        } else {
            const block = buildAiBlockEl(document, dt.getData(DRAG_MIME_AI) || '', aiPerRecipient);
            const island = block.querySelector<HTMLElement>('[data-ai-prompt]');
            range.insertNode(block);
            ensureTrail(root);
            if (island) placeCaret((nr) => nr.selectNodeContents(island), island);
            else placeCaret((nr) => nr.setStartAfter(block));
        }
        emit();
    }

    return (
        <div ref={wrapRef} className="relative">
            <div
                ref={rootRef}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline={multiline}
                aria-label={ariaLabel ?? placeholder}
                spellCheck
                className={`ot-token-editor${multiline ? '' : ' ot-token-editor--single'}`}
                style={minHeight ? { minHeight } : undefined}
                data-empty={value.trim() === '' ? 'true' : 'false'}
                data-placeholder={placeholder ?? ''}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onBeforeInput={handleBeforeInput}
                onPaste={handlePaste}
                onClick={handleClick}
                onFocus={onFocus}
                onBlur={() => setMenu(null)}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            />
            {/* Slash menu — pointerdown is swallowed so the editor keeps focus
                (and its caret) while an item is clicked. */}
            {menu && menuItems.length > 0 && (
                <div className="ot-slash-menu" role="listbox" aria-label="Insert" style={{ left: menu.x, bottom: menu.bottom }}>
                    <div className="ot-slash-hint">Insert</div>
                    {menuItems.map((item, i) => (
                        <button
                            key={item.kind === 'variable' ? item.token : item.kind}
                            type="button"
                            role="option"
                            aria-selected={i === menuIndex}
                            className={`ot-slash-item${i === menuIndex ? ' ot-slash-item--active' : ''}`}
                            onPointerDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setMenuIndex(i)}
                            onClick={() => applySlashItem(item)}
                        >
                            {item.kind === 'variable' ? (
                                <span className={variableChipClass(item.token)}>
                                    <span className="ot-chip-dot" />
                                    {item.label}
                                </span>
                            ) : (
                                <span className="ot-ai-insert" style={{ pointerEvents: 'none' }}>
                                    <Sparkles size={11} strokeWidth={2} /> {item.label}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
});

/* ── DOM utilities ── */

function closestEl(node: Node, selector: string): HTMLElement | null {
    const el = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
    return el?.closest(selector) ?? null;
}

/** Caret range for a drop point — clamped inside the editor and bumped out of
 *  bricks so nothing ever nests inside a chip or an AI box. */
function rangeFromPoint(x: number, y: number, root: HTMLElement): Range {
    type CaretDoc = Document & {
        caretRangeFromPoint?: (x: number, y: number) => Range | null;
        caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    };
    const doc = document as CaretDoc;
    let r: Range | null = null;
    if (doc.caretRangeFromPoint) {
        r = doc.caretRangeFromPoint(x, y);
    } else if (doc.caretPositionFromPoint) {
        const pos = doc.caretPositionFromPoint(x, y);
        if (pos) {
            r = document.createRange();
            r.setStart(pos.offsetNode, pos.offset);
        }
    }
    if (!r || !root.contains(r.startContainer)) {
        r = document.createRange();
        const trail = root.querySelector(':scope > [data-ote-trail]');
        if (trail) r.setStartBefore(trail);
        else {
            r.selectNodeContents(root);
            r.collapse(false);
        }
    }
    r.collapse(true);
    const host = closestEl(r.startContainer, '[data-token], [data-ai-block], [data-ai-adv-block]');
    if (host && root.contains(host) && host !== root) {
        r = document.createRange();
        r.setStartAfter(host);
        r.collapse(true);
    }
    return r;
}

/** Keep exactly one trailing <br> so the caret can sit on a final empty line
 *  (with pre-wrap, a trailing "\n" alone doesn't render one). The serializer
 *  skips it via data-ote-trail. */
function ensureTrail(root: HTMLElement) {
    const trails = root.querySelectorAll('[data-ote-trail]');
    trails.forEach((t, i) => {
        if (i < trails.length - 1) t.remove();
    });
    let trail = root.querySelector(':scope > [data-ote-trail]');
    if (!trail) {
        const br = document.createElement('br');
        br.dataset.oteTrail = '';
        root.append(br);
        return;
    }
    if (trail !== root.lastChild) root.append(trail);
}
